import styled, { css } from "styled-components";
import { Colors, GrayColors } from "@smartsuite/types";

/* selection ring + its enter/exit animation, shared by the block card and
   the branch header cards; matches the action cards' transitions */
const selectionRing = css<{ $isActive?: boolean }>`
  box-shadow: ${({ $isActive }) =>
    $isActive === true
      ? "0 0 0 1.5px var(--solution-primary-color), 0 1px 2px rgba(0, 0, 30, 0.1), 0 2px 4px rgba(0, 0, 30, 0.1)"
      : "0 1px 2px rgba(0, 0, 30, 0.1), 0 2px 4px rgba(0, 0, 30, 0.1)"};
  transition: box-shadow 150ms ease-out;
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export const StyledBranchesCard = styled.div<{ $isActive?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  /* border-box so the 350px matches a branch header card's rendered width
     (that one is sized by its 400px column minus 2x25px margins, padding
     inside) -- keeps the block card and the columns under it the same width */
  box-sizing: border-box;
  width: 350px;
  /* full action-card height (Figma), content vertically centered */
  min-height: 82px;
  /* self-centers within the shrink-to-fit sidebar container so the row's
     ::before stub (anchored at the row's own horizontal center) lines up
     under the card once the branch fan is wider than the card itself */
  margin: 10px auto 0;
  padding: 0 17px;
  cursor: pointer;

  background: #ffffff;
  border: none;
  border-radius: 8px;
  ${selectionRing}
`;

/** Text column of the block card: "Action" caption + block name. */
export const StyledBranchesBlockText = styled.div`
  display: flex;
  flex: 1;
  min-width: 0;
  flex-direction: column;
  gap: 2px;

  .branches-block-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const StyledBranchColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  /* min-width keeps sibling columns on the 400px grid; max-content lets a
     column GROW when it hosts a nested branches fan wider than itself, so
     sibling subtrees never overlap (nested Advanced Logic, Етап 7) */
  min-width: 400px;
  width: max-content;
  flex-shrink: 0;
`;

export const StyledBranchHeaderCard = styled.div<{ $isActive?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  gap: 15px;
  box-sizing: border-box;
  width: 350px;
  min-height: 64px;
  padding: 0 17px;
  /* gap between the header card and the branch's first action card */
  margin-bottom: 10px;
  cursor: pointer;

  background: #ffffff;
  border-radius: 8px;
  ${selectionRing}
`;

export const StyledBranchHeaderIcon = styled.div`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;

  background: #ffffff;
  border: 1px solid #e9e9e9;
  border-radius: 50%;
`;

/**
 * The Conditional Logic block's icon circle. Unlike the branch header icons
 * (white), the block reuses the SmartSuite action-icon accent from Figma:
 * a light-blue fill so the block reads as an action in the sequence.
 */
export const StyledBranchesBlockIcon = styled(StyledBranchHeaderIcon)`
  background: #eef3fc;
  border: 1px solid rgba(85, 138, 224, 0.2);
`;

export const StyledBranchHeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

/** Pins the kebab to the right edge of the branch header card. */
export const StyledBranchMenuSlot = styled.span`
  margin-left: auto;
`;

export const StyledBranchHeaderLabel = styled.span<{ $hasError?: boolean }>`
  font-size: 10px;
  font-weight: 500;
  line-height: 15px;
  color: ${({ $hasError }) => ($hasError === true ? Colors.bRed : "#2e3538")};
`;

export const StyledBranchHeaderSummary = styled.span<{
  $hasConditions: boolean;
}>`
  font-size: 13px;
  line-height: 18px;
  /* Figma: branches with conditions show the summary in the primary text
     colour; "No Conditions"/else stay muted grey. */
  color: ${({ $hasConditions }) => ($hasConditions ? "#2e3538" : "#878b92")};

  .branches-condition-count {
    font-weight: 600;
  }
`;

const connectorColor = GrayColors["G-4"];
const CONNECTOR_ZONE = 40; // px, zone for the arrows between the block and headers
const COLUMN_GAP = 10;

export const StyledBranchRow = styled.div`
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: ${COLUMN_GAP}px;
  width: max-content;
  padding-top: ${CONNECTOR_ZONE + 10}px; /* 10px stub + arrow zone */
  margin-top: 10px;

  /* stub from the bottom of the block card down to the horizontal */
  &::before {
    content: "";
    position: absolute;
    top: -10px;
    left: 50%;
    height: 20px;
    border-left: 1px dashed ${connectorColor};
  }
`;

export const StyledBranchColumnWrap = styled.div<{
  isFirst: boolean;
  isLast: boolean;
}>`
  position: relative;

  /* horizontal segment (+ rounded corner at the edges) */
  &::before {
    content: "";
    position: absolute;
    top: -${CONNECTOR_ZONE}px;
    bottom: 100%;
    left: ${(p) => (p.isFirst ? "50%" : `-${COLUMN_GAP}px`)};
    right: ${(p) => (p.isLast ? "50%" : `-${COLUMN_GAP}px`)};
    border-top: 1px dashed ${connectorColor};
    ${(p) =>
      p.isFirst &&
      `border-left: 1px dashed ${connectorColor};
       border-top-left-radius: 8px;`}
    ${(p) =>
      p.isLast &&
      `border-right: 1px dashed ${connectorColor};
       border-top-right-radius: 8px;`}
  }

  /* vertical stub down to the header -- only for middle columns:
     the corner's border-left/right already plays that role on the edges */
  &::after {
    content: "";
    position: absolute;
    top: -${CONNECTOR_ZONE}px;
    left: 50%;
    height: ${CONNECTOR_ZONE - 6}px;
    border-left: ${(p) =>
      p.isFirst || p.isLast ? "none" : `1px dashed ${connectorColor}`};
  }
`;

/* down-arrow into the center of the column, under the stub/corner's end */
export const StyledConnectorArrow = styled.span`
  position: absolute;
  top: -8px;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 4px solid transparent;
  border-right: 4px solid transparent;
  border-top: 5px solid ${connectorColor};
`;
